/* eslint-disable @typescript-eslint/explicit-function-return-type */
import * as React from 'react';
import { Autocomplete } from '@material-ui/lab';
import { FormLabel, TextField, makeStyles, Paper, ChipProps } from '@material-ui/core';
import { useSelector } from 'react-redux';

import { useCompanies } from 'entities/organization/useCompanies';
import { usePartners } from 'entities/organization/usePartners';
import Chip from 'retired/Chip';
import { Privileges } from 'utils/privileges';
import { selectUser } from 'entities/user/selectors';

export const useStyles = makeStyles(() => ({
  inputRoot: {
    minHeight: 45,
  },
}));

export const CompanySelectInput = ({
  values = [],
  onChange,
  excludeExisting = false,
}: {
  values: string[];
  onChange: (companyIds: string[]) => void;
  excludeExisting?: boolean;
}): JSX.Element => {
  const classes = useStyles();
  const { data: companies, loading, error } = useCompanies();
  const user = useSelector(selectUser);
  const { data: existingPartners } = usePartners(
    undefined,
    excludeExisting || !user?.hasPrivilege(Privileges.PARTNER_VIEW)
  );
  const existingPartnerIds = existingPartners.map((p) => p.id);
  const options = [
    ...(existingPartners || []).map((partner) => ({
      id: partner.id,
      name: partner.name,
    })),
    ...(companies || []).map((company) => ({
      id: company._id,
      name: company.name,
    })),
  ];

  if (error) {
    return (
      <FormLabel error>
        An error occurred while trying to load companies data, please try again.
      </FormLabel>
    );
  }
  return (
    <Autocomplete
      classes={classes}
      multiple
      loading={loading}
      loadingText="Loading…"
      filterOptions={(opts, { getOptionLabel, inputValue }) =>
        opts.filter((o) =>
          getOptionLabel(o).toLocaleLowerCase().includes(inputValue.toLocaleLowerCase())
        )
      }
      value={options.filter((i) => [...existingPartnerIds, ...values].includes(i.id))}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => {
          const isDisabled = Boolean(existingPartners.find((i) => i.id === option.id));
          const { onDelete, onClick, ...tagProps }: Partial<ChipProps> = getTagProps({ index });

          return (
            <Chip
              {...tagProps}
              onClick={isDisabled ? undefined : onClick}
              onDelete={isDisabled ? undefined : onDelete}
              disabled={isDisabled}
              label={option.name}
            />
          );
        })
      }
      PaperComponent={(paperProps) => <Paper {...paperProps} square />}
      options={options}
      getOptionLabel={(option) => option.name}
      renderInput={(params) => (
        <TextField {...params} variant="standard" label="Search companies by name" placeholder="" />
      )}
      onChange={(_event, value) => {
        onChange(value.map((i) => i.id).filter((i) => !existingPartnerIds.includes(i)));
      }}
    />
  );
};
